import { useEffect, useState, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import {
  ClipboardCheck,
  Plus,
  Loader2,
  Download,
  Send,
  CheckCircle2,
  XCircle,
  FileSignature,
  Clock,
  History,
  RotateCcw,
  Coins,
} from "lucide-react";
import { Input } from "../components/ui/input";
import { ApprovalFormDialog } from "../components/approvals/ApprovalFormDialog";
import { SignaturePad } from "../components/approvals/SignaturePad";
import { ApprovalDetailDialog } from "../components/approvals/ApprovalDetailDialog";
import { STATUS_LABELS, fmtEUR, EMPTY_APPROVAL } from "../components/approvals/approvalsConstants";
import { safeDownloadBlob } from "../lib/downloadUtils";

const API = process.env.REACT_APP_BACKEND_URL;
const DIRECTOR_EMAIL = "dredondo@valorizasm.com";

export default function ApprovalsPage() {
  const { token, user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null); // approval to edit
  const [saving, setSaving] = useState(false);
  const [signOpen, setSignOpen] = useState(false);
  const [signTarget, setSignTarget] = useState(null);
  const [signNotes, setSignNotes] = useState("");
  const [signDataUrl, setSignDataUrl] = useState(null);
  const [signLoading, setSignLoading] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailItem, setDetailItem] = useState(null);
  const [amountsTarget, setAmountsTarget] = useState(null);
  const [amountsOpen, setAmountsOpen] = useState(false);
  const [amountsForm, setAmountsForm] = useState({ presupuesto_total: 0, subvencion: 0 });
  const [amountsSaving, setAmountsSaving] = useState(false);

  const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };

  const fetchData = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/approvals`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setItems(await res.json());
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const openCreate = () => {
    setEditing(null);
    setFormOpen(true);
  };

  const handleSave = async (payload) => {
    setSaving(true);
    try {
      const url = editing ? `${API}/api/approvals/${editing.id}` : `${API}/api/approvals`;
      const method = editing ? "PUT" : "POST";
      const res = await fetch(url, { method, headers, body: JSON.stringify(payload) });
      if (!res.ok) throw new Error("Error al guardar");
      toast.success(editing ? "Ficha actualizada" : "Ficha creada como borrador");
      setFormOpen(false);
      setEditing(null);
      fetchData();
    } catch (e) {
      toast.error(e.message || "Error al guardar la ficha");
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadPdf = async (item) => {
    try {
      await safeDownloadBlob(
        `${API}/api/approvals/${item.id}/pdf`,
        `Aprobacion_${item.project_name.replace(/\s+/g, "_")}.pdf`,
        token
      );
      toast.success("PDF descargado");
    } catch (e) {
      toast.error(e.message || "Error al descargar el PDF");
    }
  };

  const handleSendForSignature = async (item) => {
    if (!window.confirm(`¿Enviar la ficha "${item.project_name}" a ${DIRECTOR_EMAIL} para firma?`)) return;
    try {
      const res = await fetch(`${API}/api/approvals/${item.id}/send-for-signature`, {
        method: "POST",
        headers,
      });
      if (!res.ok) throw new Error("Error al enviar");
      toast.success(`Ficha enviada a ${DIRECTOR_EMAIL} con el PDF adjunto`);
      fetchData();
    } catch (e) {
      toast.error(e.message || "Error al enviar para firma");
    }
  };

  const handleResubmit = async (item) => {
    if (!window.confirm(`Reabrir "${item.project_name}" para corregir y reenviar (creará la versión ${(item.version || 1) + 1})?`)) return;
    try {
      const res = await fetch(`${API}/api/approvals/${item.id}/resubmit`, { method: "POST", headers });
      if (!res.ok) throw new Error("Error al reabrir la ficha");
      toast.success("Ficha reabierta como borrador. Edítala y reenvíala.");
      fetchData();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const openDetail = (item) => {
    setDetailItem(item);
    setDetailOpen(true);
  };

  const openAmounts = (item) => {
    setAmountsTarget(item);
    setAmountsForm({
      presupuesto_total: item.presupuesto_total || 0,
      subvencion: item.financial?.subvencion || 0,
    });
    setAmountsOpen(true);
  };

  const handleSaveAmounts = async () => {
    setAmountsSaving(true);
    try {
      const res = await fetch(`${API}/api/approvals/${amountsTarget.id}/amounts`, {
        method: "PATCH",
        headers,
        body: JSON.stringify({
          presupuesto_total: amountsForm.presupuesto_total,
          subvencion: amountsForm.subvencion,
          source: "excel_i_d_2026",
        }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || "Error corrigiendo importes");
      }
      toast.success("Importes corregidos sin afectar la firma");
      setAmountsOpen(false);
      fetchData();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setAmountsSaving(false);
    }
  };

  const openSign = (item, decision) => {
    setSignTarget({ ...item, _decision: decision });
    setSignNotes("");
    setSignDataUrl(null);
    setSignOpen(true);
  };

  const handleSign = async () => {
    setSignLoading(true);
    try {
      const res = await fetch(`${API}/api/approvals/${signTarget.id}/sign`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          decision: signTarget._decision,
          notes: signNotes,
          name: user?.full_name || "",
          signature_data_url: signTarget._decision === "approved" ? signDataUrl : null,
        }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || "Error firmando");
      }
      toast.success(signTarget._decision === "approved" ? "Ficha aprobada" : "Ficha rechazada");
      setSignOpen(false);
      fetchData();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSignLoading(false);
    }
  };

  const isDirector =
    user?.email?.toLowerCase() === DIRECTOR_EMAIL.toLowerCase() || user?.role === "admin";

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in" data-testid="approvals-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <ClipboardCheck className="w-8 h-8 text-primary" />
            Fichas de Aprobación
          </h1>
          <p className="text-muted-foreground mt-1">
            Crea fichas de aprobación de proyectos I+D+i y envíalas a {DIRECTOR_EMAIL} para firma digital.
          </p>
        </div>
        <Button onClick={openCreate} data-testid="new-approval-btn">
          <Plus className="w-4 h-4 mr-1" />Nueva ficha
        </Button>
      </div>

      {/* KPI strip */}
      <div className="grid gap-3 md:grid-cols-4">
        {Object.entries(STATUS_LABELS).map(([key, info]) => {
          const count = items.filter((i) => i.status === key).length;
          return (
            <Card key={key}>
              <CardContent className="p-4 flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${info.color.split(" ").slice(0, 2).join(" ")}`}>
                  {key === "approved" ? <CheckCircle2 className="w-5 h-5" /> :
                    key === "rejected" ? <XCircle className="w-5 h-5" /> :
                      key === "pending_signature" ? <Clock className="w-5 h-5" /> :
                        <FileSignature className="w-5 h-5" />}
                </div>
                <div>
                  <div className="text-2xl font-bold">{count}</div>
                  <div className="text-xs text-muted-foreground">{info.label}</div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* List */}
      {items.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <ClipboardCheck className="w-12 h-12 mx-auto mb-4 opacity-30 text-muted-foreground" />
            <h3 className="font-medium mb-2">Aún no hay fichas de aprobación</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Crea tu primera ficha y envíala a David Redondo para firma.
            </p>
            <Button onClick={openCreate} data-testid="empty-create-approval-btn">
              <Plus className="w-4 h-4 mr-1" />Crear primera ficha
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3">
          {items.map((item) => {
            const info = STATUS_LABELS[item.status] || STATUS_LABELS.draft;
            return (
              <Card key={item.id} data-testid={`approval-card-${item.id}`}>
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <CardTitle className="text-lg flex items-center gap-2 flex-wrap">
                        {item.project_name}
                        <Badge variant="outline" className={info.color}>
                          {info.label}
                        </Badge>
                        {(item.version || 1) > 1 && (
                          <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                            v{item.version}
                          </Badge>
                        )}
                      </CardTitle>
                      <CardDescription className="line-clamp-2 mt-1">
                        {item.objective || "Sin objetivo"}
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3 pt-0">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Presupuesto</div>
                      <div className="font-bold">{fmtEUR(item.presupuesto_total)}</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Duración</div>
                      <div className="font-bold">{item.duration_months || "—"} meses</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Subvención</div>
                      <div className="font-bold">
                        {item.financial?.subvencion ? fmtEUR(item.financial.subvencion) : (
                          <span className="text-muted-foreground font-normal">Sin subvención</span>
                        )}
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Técnico</div>
                      <div className="font-medium truncate">{item.technician?.name || "—"}</div>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2 pt-2 border-t">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openDetail(item)}
                      data-testid={`approval-history-${item.id}`}
                    >
                      <History className="w-3.5 h-3.5 mr-1" />Historial
                      {item.history?.length > 0 && (
                        <span className="ml-1 text-[10px] opacity-70">({item.history.length})</span>
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDownloadPdf(item)}
                      data-testid={`approval-download-${item.id}`}
                    >
                      <Download className="w-3.5 h-3.5 mr-1" />PDF
                    </Button>
                    {item.status === "draft" && (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => { setEditing(item); setFormOpen(true); }}
                          data-testid={`approval-edit-${item.id}`}
                        >
                          Editar
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleSendForSignature(item)}
                          data-testid={`approval-send-${item.id}`}
                        >
                          <Send className="w-3.5 h-3.5 mr-1" />Enviar para firma
                        </Button>
                      </>
                    )}
                    {item.status === "pending_signature" && isDirector && (
                      <>
                        <Button
                          size="sm"
                          className="bg-green-600 hover:bg-green-700"
                          onClick={() => openSign(item, "approved")}
                          data-testid={`approval-approve-${item.id}`}
                        >
                          <CheckCircle2 className="w-3.5 h-3.5 mr-1" />Aprobar y firmar
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => openSign(item, "rejected")}
                          data-testid={`approval-reject-${item.id}`}
                        >
                          <XCircle className="w-3.5 h-3.5 mr-1" />Rechazar
                        </Button>
                      </>
                    )}
                    {item.status === "pending_signature" && user?.role === "admin" && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-teal-300 text-teal-700 hover:bg-teal-50"
                        onClick={() => openAmounts(item)}
                        data-testid={`approval-edit-amounts-${item.id}`}
                      >
                        <Coins className="w-3.5 h-3.5 mr-1" />Editar importes
                      </Button>
                    )}
                    {item.status === "pending_signature" && !isDirector && (
                      <span className="text-xs text-muted-foreground self-center px-2">
                        ⏳ Esperando firma de {DIRECTOR_EMAIL}
                      </span>
                    )}
                    {item.status === "rejected" && (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-purple-300 text-purple-700 hover:bg-purple-50"
                          onClick={() => handleResubmit(item)}
                          data-testid={`approval-resubmit-${item.id}`}
                        >
                          <RotateCcw className="w-3.5 h-3.5 mr-1" />Reabrir y revisar
                        </Button>
                        <span className="text-xs text-red-700 font-medium self-center px-2">
                          ✕ Rechazada el {item.director?.signed_at?.slice(0, 10)}
                        </span>
                      </>
                    )}
                    {item.status !== "rejected" && item.status === "approved" && (
                      <span className="text-xs text-green-700 font-medium self-center px-2">
                        ✓ Aprobada el {item.director?.signed_at?.slice(0, 10)} por {item.director?.name}
                      </span>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <ApprovalFormDialog
        open={formOpen}
        onOpenChange={(o) => { setFormOpen(o); if (!o) setEditing(null); }}
        initialData={editing || EMPTY_APPROVAL}
        onSave={handleSave}
        saving={saving}
        token={token}
      />

      <ApprovalDetailDialog
        open={detailOpen}
        onOpenChange={setDetailOpen}
        approval={detailItem}
        token={token}
      />

      <Dialog open={amountsOpen} onOpenChange={setAmountsOpen}>
        <DialogContent className="max-w-md" data-testid="amounts-dialog">
          <DialogHeader>
            <DialogTitle>Editar importes — {amountsTarget?.project_name}</DialogTitle>
            <DialogDescription>
              Corrige presupuesto/subvención sin rechazar la firma. Queda registrado en el historial.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs text-muted-foreground">Presupuesto (€)</label>
              <Input
                type="number"
                value={amountsForm.presupuesto_total}
                onChange={(e) => setAmountsForm((f) => ({ ...f, presupuesto_total: parseFloat(e.target.value) || 0 }))}
                data-testid="amounts-presupuesto-input"
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Subvención (€)</label>
              <Input
                type="number"
                value={amountsForm.subvencion}
                onChange={(e) => setAmountsForm((f) => ({ ...f, subvencion: parseFloat(e.target.value) || 0 }))}
                data-testid="amounts-subvencion-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAmountsOpen(false)}>Cancelar</Button>
            <Button onClick={handleSaveAmounts} disabled={amountsSaving} data-testid="amounts-save-btn">
              {amountsSaving ? "Guardando..." : "Guardar importes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={signOpen} onOpenChange={setSignOpen}>
        <DialogContent className="max-w-lg" data-testid="sign-dialog">
          <DialogHeader>
            <DialogTitle>
              {signTarget?._decision === "approved" ? "Aprobar y firmar" : "Rechazar ficha"}
            </DialogTitle>
            <DialogDescription>
              {signTarget?._decision === "approved"
                ? `Confirma la aprobación de "${signTarget?.project_name}". El técnico será notificado por email.`
                : `Indica el motivo de rechazo de "${signTarget?.project_name}".`}
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={signNotes}
            onChange={(e) => setSignNotes(e.target.value)}
            rows={3}
            placeholder={
              signTarget?._decision === "approved"
                ? "Comentarios opcionales sobre la aprobación..."
                : "Indica los motivos del rechazo (obligatorio)..."
            }
            data-testid="sign-notes"
          />
          {signTarget?._decision === "approved" && (
            <div className="space-y-2">
              <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Firma digital (opcional)
              </div>
              <SignaturePad onChange={setSignDataUrl} />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSignOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleSign}
              disabled={signLoading || (signTarget?._decision === "rejected" && !signNotes.trim())}
              variant={signTarget?._decision === "rejected" ? "destructive" : "default"}
              data-testid="sign-confirm-btn"
            >
              {signLoading ? "Procesando..." : signTarget?._decision === "approved" ? "Confirmar aprobación" : "Confirmar rechazo"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
